import React, { useState } from 'react';

/**
 * DistrictLayerDrawer – settings panel for the static_only campaign mode.
 */

const COLORS = {
  pageBg: '#fbf8ef',
  dark: '#41392f',
  card: '#ffffff',
  hover: '#faf6ee',
  textPrimary: '#41392f',
  textSecondary: '#90795b',
  gold: '#d0b778',
  matchHq: '#da9828',
  hiddenGem: '#b3a637',
  link: '#2a6496',
  border: '#f0f0f0'
};

const ToggleSwitch = ({ checked, onChange, color, label }) => (
  <label
    style={{
      position: 'relative',
      display: 'inline-block',
      width: '40px',
      height: '22px',
      flexShrink: 0
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <input
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={onChange}
      style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
      aria-label={label}
    />
    <span style={{
      position: 'absolute',
      cursor: 'pointer',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: checked ? (color || COLORS.matchHq) : '#ccc',
      transition: '0.2s',
      borderRadius: '999px'
    }} />
    <span style={{
      position: 'absolute',
      height: '18px',
      width: '18px',
      left: checked ? '20px' : '2px',
      top: '2px',
      backgroundColor: 'white',
      transition: '0.2s',
      borderRadius: '50%',
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
    }} />
  </label>
);

const DistrictLayerDrawer = ({
  districts,
  onZoomToDistrict,
  showMatchHQ,
  showHiddenGem,
  onToggleMatchHQ,
  onToggleHiddenGem,
  showWatchParties,
  onToggleWatchParties,
  layerDescriptions
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState({});

  const openDrawer = () => {
    setIsOpen(true);
    setIsOpening(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsOpening(false);
      });
    });
  };

  const closeDrawer = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const toggleDetails = (key) => {
    setExpandedDetails(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const desc = layerDescriptions || {};

  return (
    <>
      {/* Small tray tab at the bottom */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: COLORS.dark,
          borderTop: `1px solid ${COLORS.border}`,
          padding: '10px 16px',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}
      >
        <button
          type="button"
          onClick={openDrawer}
          style={{
            padding: '8px 20px',
            borderRadius: '8px',
            border: `1px solid ${COLORS.gold}`,
            background: COLORS.dark,
            color: COLORS.gold,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
          aria-label="Legend and Settings"
        >
          <span style={{ fontSize: '16px' }}>🧭</span>
          <span>Legend and Settings</span>
        </button>
        <a
          href="#how-to-play"
          onClick={(e) => { e.preventDefault(); setShowHowToPlay(true); }}
          style={{
            color: 'rgb(208, 183, 120)',
            fontWeight: 700,
            fontSize: '14px',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
        >
          How to Play
        </a>
      </div>

      {/* How to Play modal */}
      {showHowToPlay && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(65, 57, 47, 0.5)'
            }}
            onClick={() => setShowHowToPlay(false)}
          />
          <div
            style={{
              position: 'relative',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              backgroundColor: COLORS.pageBg,
              borderRadius: '16px',
              padding: '24px',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(65, 57, 47, 0.25)'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COLORS.textPrimary }}>
                How to Play
              </h2>
              <button
                type="button"
                onClick={() => setShowHowToPlay(false)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.card,
                  color: COLORS.textPrimary,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
            <div style={{ color: COLORS.textPrimary, fontSize: '14px', lineHeight: 1.6 }}>
              <p style={{ marginBottom: '15px'}}>Collect a minimum of six stamps across two fan zones between June 11-July 19!</p>
              <ol>
                <li style={{ marginBottom: '10px'}}>
                  <strong>Hit the Trail</strong><br />
                  Pick up a Muni Goal Rush game card from any of the participating businesses listed below or print out your own copy at home to get started. Need help planning your trip? Our Muni Goal Rush fun map makes it easy to find the nearest bus, cable car, light rail, or streetcar headed to a fan zone.
                </li>

                <li style={{ marginBottom: '10px'}}><strong>Claim Your Stamps</strong> (three per zone)<br />
                  <ul style={{ paddingLeft: '25px'}}>
                    <li>Match HQ: Get a stamp from one Match HQ in each fan zone — your go-to spots to catch the game.</li>
                    <li>Hidden Gems: Get a stamp from two Hidden Gem businesses in the same zone.</li>
                  </ul>
                </li>
                <li>
                  <strong>Enter to Win</strong><br />
                  Once you've collected a minimum of six stamps across two fan zones, submit your entry on the <a target='_blank' href="https://www.sfmta.com/GoalRush#submit">Muni Goal Rush webpage</a>. You'll need to upload a photo of your stamped game card to complete the process.
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Modal overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center'
          }}
        >
          {/* Backdrop */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(65, 57, 47, 0.5)',
              opacity: (isClosing || isOpening) ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
            onClick={closeDrawer}
          />

          {/* Panel */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '95vh',
              backgroundColor: COLORS.pageBg,
              borderRadius: '16px 16px 0 0',
              padding: '20px',
              overflowY: 'auto',
              boxShadow: '0 -4px 20px rgba(65, 57, 47, 0.2)',
              transform: (isClosing || isOpening) ? 'translateY(100%)' : 'translateY(0)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COLORS.textPrimary }}>
                Legend and Settings
              </h2>
              <button
                type="button"
                onClick={closeDrawer}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: `1px solid ${COLORS.border}`,
                  background: COLORS.card,
                  color: COLORS.textPrimary,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                aria-label="Close"
              >
                Close
              </button>
            </div>

            {/* === Group 1: Fun Zones + Type Filters === */}
            <div style={{
              backgroundColor: COLORS.card,
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              padding: '16px',
              marginBottom: '12px'
            }}>
              {/* Group heading */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: COLORS.textPrimary }}>
                  Muni Goal Rush Game
                </h3>
                <a
                  href="https://www.sfmta.com/GoalRush"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Learn more about how to play the Muni Goal Rush Game"
                  style={{ fontSize: '12px', color: COLORS.link, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                >
                  What's this?
                </a>
              </div>

              {/* Section label */}
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '10px'
              }}>
                Show on map
              </div>

              {/* Type toggles */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                {/* Match HQ */}
                <div style={{
                  borderRadius: '8px',
                  backgroundColor: showMatchHQ ? COLORS.pageBg : COLORS.hover,
                  border: showMatchHQ ? `1px solid ${COLORS.matchHq}` : `1px solid ${COLORS.border}`,
                  overflow: 'hidden'
                }}>
                  <div
                    onClick={onToggleMatchHQ}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: showMatchHQ ? COLORS.textPrimary : COLORS.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span>⚽</span><span>Match HQ</span>
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <a
                        href="#details"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDetails('matchHQ'); }}
                        style={{ fontSize: '12px', color: COLORS.link, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {expandedDetails.matchHQ ? 'hide details' : 'show details'}
                      </a>
                      <ToggleSwitch
                        checked={showMatchHQ}
                        onChange={onToggleMatchHQ}
                        color={COLORS.matchHq}
                        label="Toggle Match HQ pins"
                      />
                    </div>
                  </div>
                  {expandedDetails.matchHQ && desc.matchHQ && (
                    <div
                      style={{ padding: '0 12px 10px 12px', fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: desc.matchHQ }}
                    />
                  )}
                </div>

                {/* Hidden Gem */}
                <div style={{
                  borderRadius: '8px',
                  backgroundColor: showHiddenGem ? COLORS.pageBg : COLORS.hover,
                  border: showHiddenGem ? `1px solid ${COLORS.hiddenGem}` : `1px solid ${COLORS.border}`,
                  overflow: 'hidden'
                }}>
                  <div
                    onClick={onToggleHiddenGem}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: showHiddenGem ? COLORS.textPrimary : COLORS.textSecondary, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span>💎</span><span>Hidden Gem</span>
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <a
                        href="#details"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDetails('hiddenGem'); }}
                        style={{ fontSize: '12px', color: COLORS.link, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {expandedDetails.hiddenGem ? 'hide details' : 'show details'}
                      </a>
                      <ToggleSwitch
                        checked={showHiddenGem}
                        onChange={onToggleHiddenGem}
                        color={COLORS.hiddenGem}
                        label="Toggle Hidden Gem pins"
                      />
                    </div>
                  </div>
                  {expandedDetails.hiddenGem && desc.hiddenGem && (
                    <div
                      style={{ padding: '0 12px 10px 12px', fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: desc.hiddenGem }}
                    />
                  )}
                </div>
              </div>

              {/* Divider */}
              <div style={{ borderTop: `1px solid ${COLORS.border}`, marginBottom: '12px' }} />

              {/* Section label */}
              <div style={{
                fontSize: '12px',
                fontWeight: 600,
                color: COLORS.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '12px'
              }}>
                Zoom to a Fun Zone
              </div>

              {/* District zoom buttons */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                justifyContent: 'center'
              }}>
                {districts.map((district) => (
                  <button
                    key={district.id}
                    type="button"
                    onClick={() => {
                      onZoomToDistrict(district.id);
                      closeDrawer();
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.gold}`,
                      backgroundColor: COLORS.pageBg,
                      color: COLORS.textPrimary,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {district.name}
                  </button>
                ))}
              </div>
            </div>

            {/* === Group 2: Watch Parties & Entertainment Zones === */}
            <div style={{
              backgroundColor: COLORS.card,
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              padding: '16px'
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 700, color: COLORS.textPrimary }}>
                More ways to enjoy the World Cup
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Watch Parties */}
                <div style={{
                  borderRadius: '8px',
                  backgroundColor: COLORS.pageBg,
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textPrimary, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span>📺</span><span>Watch Parties</span>
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <a
                        href="#details"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDetails('watchParties'); }}
                        style={{ fontSize: '12px', color: COLORS.link, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {expandedDetails.watchParties ? 'hide details' : 'show details'}
                      </a>
                      <ToggleSwitch
                        checked={showWatchParties}
                        onChange={onToggleWatchParties}
                        color={COLORS.matchHq}
                        label="Toggle Watch Parties"
                      />
                    </div>
                  </div>
                  {expandedDetails.watchParties && desc.watchParties && (
                    <div
                      style={{ padding: '0 12px 10px 12px', fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: desc.watchParties }}
                    />
                  )}
                </div>

                {/* Entertainment Zones */}
                <div style={{
                  borderRadius: '8px',
                  backgroundColor: COLORS.pageBg,
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden'
                }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: COLORS.textPrimary, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <span>🍸</span><span>Entertainment Zones</span>
                    </span>
                    <div style={{ display: 'none', alignItems: 'center', gap: '10px' }}>
                      <a
                        href="#details"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDetails('entertainmentZones'); }}
                        style={{ fontSize: '12px', color: COLORS.link, fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        {expandedDetails.entertainmentZones ? 'hide details' : 'show details'}
                      </a>
                      <ToggleSwitch
                        checked={true}
                        onChange={() => { }}
                        color={COLORS.matchHq}
                        label="Toggle Entertainment Zones"
                      />
                    </div>
                  </div>
                  {expandedDetails.entertainmentZones && desc.entertainmentZones && (
                    <div
                      style={{ padding: '0 12px 10px 12px', fontSize: '12px', color: COLORS.textSecondary, lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: desc.entertainmentZones }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DistrictLayerDrawer;
